/**
 * Approval routing engine (Time Approval Routing, Part 1). Pure, no I/O — the
 * service hands it plain data (the charge-object -> approver map + the worker's
 * manager) and it decides, deterministically:
 *
 *   - resolveEntryApprover : who approves a single timesheet line (D2 order).
 *   - routeSubmission      : group a week's lines into one segment per approver.
 *   - deriveSubmissionStatus : roll segment decisions back up to the submission.
 *   - applyDelegation       : swap an approver for an active delegate (PR5).
 *
 * Manager mode never calls this (the submission routes whole to the worker's
 * manager exactly as before). It is only exercised in charge-object mode.
 */
import { findActiveDelegation } from './supervisorDelegation'
import type { SupervisorDelegation } from '../types/manufacturing-time.types'

/** Where a line's routed approver came from (for transparency + the segment UI). */
export type EntryApproverSource = 'charge_object' | 'manager_fallback'

export interface RoutingEntry {
  id: string
  /** The entry's charge code id (== the SAP charge object synthetic id for SAP
   *  objects, which is also charge_object_approvers.id). */
  chargeCodeId: string | null
  hours: number
}

export interface RoutingContext {
  /** charge code id (charge_object_approvers.id) -> approver user id. */
  approverByChargeCode: Map<string, string>
  /** The worker's manager — the never-unroutable fallback. Null if unknown. */
  managerUserId: string | null
}

export interface ResolvedEntryApprover {
  userId: string
  source: EntryApproverSource
}

/**
 * Resolve the approver for one entry (D2 order):
 *   1. an explicit charge-object approver for the line's charge object, else
 *   2. the worker's manager (fallback, so nothing is unroutable).
 * Returns null only when there is no charge-object approver AND no manager.
 */
export function resolveEntryApprover(entry: RoutingEntry, ctx: RoutingContext): ResolvedEntryApprover | null {
  if (entry.chargeCodeId) {
    const explicit = ctx.approverByChargeCode.get(entry.chargeCodeId)
    if (explicit) return { userId: explicit, source: 'charge_object' }
  }
  if (ctx.managerUserId) return { userId: ctx.managerUserId, source: 'manager_fallback' }
  return null
}

export interface ApprovalSegmentPlan {
  approverUserId: string
  entryIds: string[]
  totalHours: number
  /** True when every line in this segment routed via manager fallback (no
   *  charge-object approver). Lets the UI flag "manager fallback" segments. */
  allFallback: boolean
}

export interface RoutingResult {
  segments: ApprovalSegmentPlan[]
  /** Entry ids that could not be routed at all (no approver, no manager). */
  unrouted: string[]
}

/**
 * Group a week's entries into one segment per resolved approver. Entries that
 * resolve to no one (no charge approver + no manager) are collected in
 * `unrouted` so the caller can surface the count; they never block submission.
 */
export function routeSubmission(entries: ReadonlyArray<RoutingEntry>, ctx: RoutingContext): RoutingResult {
  const byApprover = new Map<string, { entryIds: string[]; totalHours: number; fallbackCount: number }>()
  const unrouted: string[] = []

  for (const e of entries) {
    const r = resolveEntryApprover(e, ctx)
    if (!r) { unrouted.push(e.id); continue }
    const g = byApprover.get(r.userId) ?? { entryIds: [], totalHours: 0, fallbackCount: 0 }
    g.entryIds.push(e.id)
    g.totalHours += e.hours
    if (r.source === 'manager_fallback') g.fallbackCount++
    byApprover.set(r.userId, g)
  }

  const segments: ApprovalSegmentPlan[] = [...byApprover.entries()].map(([approverUserId, g]) => ({
    approverUserId,
    entryIds: g.entryIds,
    totalHours: Math.round(g.totalHours * 100) / 100,
    allFallback: g.fallbackCount === g.entryIds.length,
  }))
  return { segments, unrouted }
}

export type SegmentStatus = 'pending' | 'approved' | 'rejected'
/** App-facing submission status (matches ApprovalDecision's relevant values). */
export type DerivedSubmissionStatus = 'pending' | 'approved' | 'rejected'

/**
 * Roll segment decisions up to the submission: any rejected -> rejected; else
 * all approved (and at least one) -> approved; else still pending. With no
 * segments the submission is pending (nothing to act on yet).
 */
export function deriveSubmissionStatus(statuses: ReadonlyArray<SegmentStatus>): DerivedSubmissionStatus {
  if (statuses.length === 0) return 'pending'
  if (statuses.some((s) => s === 'rejected')) return 'rejected'
  if (statuses.every((s) => s === 'approved')) return 'approved'
  return 'pending'
}

/**
 * Swap a resolved approver for their active delegate, if any (PR5). Reuses the
 * supervisor-delegation resolver so manager mode and charge-object mode share
 * the same delegation semantics. `onDateIso` is the date the approval is acted
 * on (delegations are date-windowed).
 */
export function applyDelegation(
  approverUserId: string,
  onDateIso: string,
  delegations: ReadonlyArray<SupervisorDelegation>,
): string {
  const active = findActiveDelegation(approverUserId, onDateIso, delegations)
  return active ? active.delegateUserId : approverUserId
}
